/**
 * M Robinson PDP Reviews - Judge.me Integration
 * Integrates with Judge.me widget data with static fallback
 */

class PDPReviews extends HTMLElement {
  constructor() {
    super();
    this.gridContainer = null;
    this.checkInterval = null;
    this.checkAttempts = 0;
    this.maxAttempts = 20; // Check for 2 seconds (20 * 100ms)
  }

  connectedCallback() {
    this.gridContainer = this.querySelector('.mrobinson-pdp-reviews__grid');
    
    // Get product info from data attribute
    const reviewsData = this.querySelector('script[data-judgeme-reviews]');
    if (reviewsData) {
      try {
        const productData = JSON.parse(reviewsData.textContent);
        this.productHandle = productData.product_handle;
        this.productId = productData.product_id;
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    }

    // Check if Judge.me widget has loaded and has reviews
    this.checkForJudgeMeData();
  }

  checkForJudgeMeData() {
    // Look for Judge.me data in window object or DOM
    if (window.jdgm && window.jdgm.reviewsCache) {
      const reviews = this.getJudgeMeReviews();
      if (reviews && reviews.length > 0) {
        this.renderJudgeMeReviews(reviews.slice(0, 3));
        return;
      }
    }

    // Check if reviews are in DOM (Judge.me widget rendered)
    const judgeMeWidget = document.querySelector('.jdgm-widget');
    if (judgeMeWidget) {
      const reviews = this.extractReviewsFromWidget(judgeMeWidget);
      if (reviews && reviews.length > 0) {
        this.renderJudgeMeReviews(reviews.slice(0, 3));
        return;
      }
    }

    // Retry checking for Judge.me data
    if (this.checkAttempts < this.maxAttempts) {
      this.checkAttempts++;
      setTimeout(() => this.checkForJudgeMeData(), 100);
    } else {
      console.log('Judge.me reviews not found, using static fallback');
    }
  }

  getJudgeMeReviews() {
    // Try to get reviews from Judge.me's cache
    if (!window.jdgm || !window.jdgm.reviewsCache) return null;
    
    const cache = window.jdgm.reviewsCache;
    const productKey = this.productHandle || this.productId;
    
    if (cache[productKey]) {
      return cache[productKey].reviews || cache[productKey];
    }

    // Try to get any available reviews
    const allReviews = Object.values(cache).flat();
    if (allReviews.length > 0) {
      // Filter for 5-star reviews
      return allReviews.filter(r => r.rating === 5 || r.score === 5);
    }

    return null;
  }

  extractReviewsFromWidget(widget) {
    // Extract review data from Judge.me widget DOM if rendered
    const reviewElements = widget.querySelectorAll('.jdgm-rev');
    if (!reviewElements.length) return null;

    const reviews = [];
    reviewElements.forEach((element, index) => {
      if (index >= 3) return; // Only get top 3

      const rating = element.querySelectorAll('.jdgm-star.jdgm--on').length;
      const body = element.querySelector('.jdgm-rev__body')?.textContent?.trim();
      const name = element.querySelector('.jdgm-rev__author')?.textContent?.trim();
      const title = element.querySelector('.jdgm-rev__title')?.textContent?.trim();

      if (body && name) {
        reviews.push({ rating, body, name, title });
      }
    });

    return reviews.length > 0 ? reviews : null;
  }

  renderJudgeMeReviews(reviews) {
    // Clear existing reviews
    this.gridContainer.innerHTML = '';

    // Render Judge.me reviews
    reviews.forEach((review, index) => {
      const card = this.createReviewCard(review);
      this.gridContainer.appendChild(card);
    });
  }

  createReviewCard(review) {
    const article = document.createElement('article');
    article.className = 'mrobinson-pdp-reviews__card';
    
    // Extract reviewer name
    const reviewerName = review.reviewer?.name || review.name || 'Anonymous';
    
    // Extract location (if available in review body or use placeholder)
    const location = this.extractLocation(review) || 'Verified Customer';
    
    // Extract collection/product info
    const collection = review.product_title || 'Our Collection';
    
    // Rating
    const rating = review.rating || 5;
    
    // Review text
    const reviewText = review.body || review.title || '';
    
    // Avatar (use initials if no image)
    const avatarHtml = this.createAvatarHtml(review);

    article.innerHTML = `
      <div class="mrobinson-pdp-reviews__quote-icon">"</div>
      <div class="mrobinson-pdp-reviews__stars" aria-label="${rating} star rating">
        ${this.createStarsHtml(rating)}
      </div>
      <p class="mrobinson-pdp-reviews__quote">"${this.escapeHtml(reviewText)}"</p>
      <div class="mrobinson-pdp-reviews__profile">
        ${avatarHtml}
        <div>
          <p class="mrobinson-pdp-reviews__name">${this.escapeHtml(reviewerName)}</p>
          <p class="mrobinson-pdp-reviews__location">${this.escapeHtml(location)}</p>
          <p class="mrobinson-pdp-reviews__collection">${this.escapeHtml(collection)}</p>
        </div>
      </div>
    `;

    return article;
  }

  createStarsHtml(rating) {
    let starsHtml = '';
    for (let i = 1; i <= 5; i++) {
      const filledClass = i <= rating ? ' is-filled' : '';
      starsHtml += `<span class="mrobinson-pdp-reviews__star${filledClass}">★</span>`;
    }
    return starsHtml;
  }

  createAvatarHtml(review) {
    // Check if review has an avatar/picture URL
    if (review.reviewer?.picture_url || review.picture_url) {
      const avatarUrl = review.reviewer?.picture_url || review.picture_url;
      const reviewerName = review.reviewer?.name || review.name || 'Anonymous';
      return `<img src="${avatarUrl}" class="mrobinson-pdp-reviews__avatar" alt="${this.escapeHtml(reviewerName)}" loading="lazy">`;
    }
    
    // Fallback: Create initials avatar
    const reviewerName = review.reviewer?.name || review.name || 'Anonymous';
    const initials = this.getInitials(reviewerName);
    return `<div class="mrobinson-pdp-reviews__avatar mrobinson-pdp-reviews__avatar--initials">${initials}</div>`;
  }

  getInitials(name) {
    if (!name) return 'A';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 1).toUpperCase();
  }

  extractLocation(review) {
    // Try to extract location from review metadata
    if (review.reviewer?.location) {
      return review.reviewer.location;
    }
    
    // Check if location is in custom fields
    if (review.custom_fields) {
      const locationField = review.custom_fields.find(field => 
        field.name?.toLowerCase().includes('location') || 
        field.name?.toLowerCase().includes('city')
      );
      if (locationField) {
        return locationField.value;
      }
    }
    
    return null;
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('pdp-reviews', PDPReviews);
